import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2563eb',
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0f766e',
      light: '#14b8a6',
      dark: '#115e59',
    },
    success: { main: '#16a34a' },
    warning: { main: '#d97706' },
    error: { main: '#dc2626' },
    info: { main: '#0284c7' },
    background: {
      default: '#f1f5f9',
      paper: '#ffffff',
    },
    text: {
      primary: '#172033',
      secondary: '#64748b',
    },
    divider: '#e5e7eb',
  },
  typography: {
    fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: 13,
    h1: { fontWeight: 800, letterSpacing: '-0.035em', fontSize: '1.4rem', lineHeight: 1.25 },
    h2: { fontWeight: 800, letterSpacing: '-0.03em', fontSize: '1.3rem', lineHeight: 1.25 },
    h3: { fontWeight: 750, letterSpacing: '-0.025em', fontSize: '1.15rem', lineHeight: 1.3 },
    h4: { fontWeight: 750, letterSpacing: '-0.02em', fontSize: '1.05rem', lineHeight: 1.3 },
    h5: { fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.35 },
    h6: { fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.35 },
    subtitle1: { fontSize: '0.82rem', fontWeight: 650 },
    subtitle2: { fontSize: '0.75rem', fontWeight: 650 },
    body1: { fontSize: '0.8rem', lineHeight: 1.45 },
    body2: { fontSize: '0.76rem', lineHeight: 1.4 },
    caption: { fontSize: '0.68rem' },
    overline: { fontSize: '0.62rem' },
    button: { textTransform: 'none', fontWeight: 650, fontSize: '0.76rem' },
  },
  shape: { borderRadius: 9 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: '#f1f5f9',
          color: '#172033',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*': { boxSizing: 'border-box' },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: 999 },
        '*::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 7, minHeight: 30, padding: '4px 12px' },
        sizeSmall: { minHeight: 26, padding: '2px 9px', fontSize: '0.7rem' },
        sizeMedium: { minHeight: 30, fontSize: '0.76rem' },
        sizeLarge: { minHeight: 34, fontSize: '0.8rem' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(15,23,42,.04)',
          backgroundImage: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: '12px 14px', '&:last-child': { paddingBottom: '12px' } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          backgroundColor: '#fff',
          fontSize: '0.78rem',
          '& fieldset': { borderColor: '#dbe1ea' },
          '&:hover fieldset': { borderColor: '#a5b4fc' },
          '&.Mui-focused fieldset': { borderColor: '#4f46e5', borderWidth: 1 },
        },
        input: { padding: '7px 10px' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { fontSize: '0.75rem' } },
    },
    MuiSelect: {
      styleOverrides: { select: { padding: '7px 10px', fontSize: '0.78rem' } },
    },
    MuiTable: {
      styleOverrides: { root: { fontSize: '0.78rem' } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            background: '#f8fafc',
            color: '#475569',
            fontWeight: 700,
            fontSize: '0.68rem',
            textTransform: 'uppercase',
            letterSpacing: '.04em',
            borderBottom: '1px solid #e2e8f0',
            padding: '6px 10px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #eef2f7',
          fontSize: '0.76rem',
          padding: '6px 10px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 650, height: 24, fontSize: '0.7rem' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: '1px solid #e5e7eb',
          boxShadow: '0 24px 70px rgba(15,23,42,.18)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '0.95rem', padding: '12px 18px' } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: '12px 18px' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '10px 18px' } },
    },
    MuiTabs: {
      styleOverrides: { root: { minHeight: 36 } },
    },
    MuiTab: {
      styleOverrides: { root: { minHeight: 36, fontSize: '0.76rem', fontWeight: 650, textTransform: 'none' } },
    },
    MuiListItem: {
      styleOverrides: { root: { fontSize: '0.78rem' } },
    },
    MuiListItemText: {
      styleOverrides: { primary: { fontSize: '0.78rem' } },
    },
    MuiPaginationItem: {
      styleOverrides: { root: { fontSize: '0.76rem', minWidth: 28, height: 28 } },
    },
    MuiIconButton: {
      styleOverrides: { root: { fontSize: '1.1rem' } },
    },
  },
});

export default theme;